/**
 * Legacy segment pages Agent Studio has taken over, and the flags that swap
 * each one for a link to its Agent Studio page.
 *
 * Studio honors dev-tools flag overrides for any user but Agent Studio only for
 * admins, so a non-admin override can link into a page that bounces.
 * `AGENT_STUDIO_AUDIENCES` targeting must key on account id only: Agent Studio
 * may identify without `tier`/`createdAt`, and a mismatch bounces the linked
 * user.
 */
import type { FeatureFlag } from '@studio/feature-flag-types';

export type LinkOutFlag = Extract<
  FeatureFlag,
  'AGENT_STUDIO' | 'AGENT_STUDIO_AUDIENCES'
>;

export type LinkOutFlagValues = Record<LinkOutFlag, boolean | null>;

export type LinkOutGate = 'link-out' | 'legacy' | 'pending';

export type AgentStudioNavigation =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'link-out'; path: string };

const SEGMENTS_PATH = '/audiences/segments';

// `/audience` and `/segments` already forward to the segment list inside a
// sub-app; matching them here skips mounting it first.
const SEGMENT_LIST =
  /^\/(segments|segments-v2|audience(?:\/segments(?:-v2)?)?)\/?$/i;
const SEGMENT_DETAIL = /^\/segments(?:-v2)?\/([^/]+)\/(?:view|edit)\/?$/i;

/** The Agent Studio path for a legacy segment page, if `pathname` is one. */
export function getAgentStudioLinkOut(pathname: string): string | undefined {
  if (SEGMENT_LIST.test(pathname)) {
    return SEGMENTS_PATH;
  }

  // Agent Studio's segment page is editable and enforces edit permission
  // itself, so view and edit share a route; `new` is its create page. The
  // pathname is already percent-encoded, so encoding the id again would
  // double-encode it.
  const segmentId = SEGMENT_DETAIL.exec(pathname)?.[1];
  if (segmentId) {
    const id = segmentId.toLowerCase() === 'new' ? 'new' : segmentId;
    return `${SEGMENTS_PATH}/${id}`;
  }

  return undefined;
}

export function resolveLinkOutGate(values: (boolean | null)[]): LinkOutGate {
  if (values.includes(false)) {
    return 'legacy';
  }

  return values.every(Boolean) ? 'link-out' : 'pending';
}

/** Query and hash are dropped on purpose: legacy params mean nothing in Agent Studio. */
export function resolveAgentStudioNavigation(
  pathname: string,
  values: LinkOutFlagValues
): AgentStudioNavigation {
  const path = getAgentStudioLinkOut(pathname);
  if (!path) {
    return { kind: 'none' };
  }

  switch (
    resolveLinkOutGate([values.AGENT_STUDIO, values.AGENT_STUDIO_AUDIENCES])
  ) {
    case 'link-out':
      return { kind: 'link-out', path };
    case 'pending':
      return { kind: 'pending' };
    default:
      return { kind: 'none' };
  }
}
